import cx from 'classnames';
import {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {FilterPreviewer} from '../FilterPreviewer';
import {MultiSelect} from './MultiSelect';

import './TopFiltersAB.sass';

export const TopFiltersAB = ({
  activeFilters,
  filtersData,
  relatedCategories,
  numTemplates,
  onFilterDismiss,
  onClearAllFilters,
  seoPage,
  galleryUploadComponent,
}) => {
  // Initial state of filterKeys is so the dropdowns render right away before the data is retrieved
  const [filterKeys, setFilterKeys] = useState(['1', '2', '3', '4', '5']);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const breadcrumbs = useSelector(({galleryData}) => galleryData.breadcrumbs);
  const categoryPath = useSelector(({galleryData}) => galleryData.categoryPath);
  const hasChildren = useSelector(({galleryData}) => galleryData.hasChildren);
  const parentId = useSelector(({galleryData}) => galleryData.parentId);

  const transformArray = (array, idKey, labelKey, valueKey) =>
    array.map((item) => ({
      id: item[idKey],
      label: item[labelKey],
      value: item[valueKey],
    }));

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const relatedCats = useMemo(() => {
    const transformedCats = transformArray(
      relatedCategories,
      'categoryId',
      'displayName',
      'categoryPath'
    );
    return transformedCats.sort((a, b) => {
      if (a.label === selectedCategory) {
        return -1;
      }
      if (b.label === selectedCategory) {
        return 1;
      }
      return 0;
    });
  }, [relatedCategories, selectedCategory]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlSelectedCategory = urlParams.get('selectedCat');
    if (urlSelectedCategory !== null) {
      setSelectedCategory(urlSelectedCategory);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    const pathname = breadcrumbs?.slice(-1)[0]?.href;
    const parentCategoryPath = hasChildren && parentId ? `${categoryPath}/` : '';

    relatedCategories.forEach((cat) => {
      const matchingPath = `${pathname}/${parentCategoryPath}${cat.categoryPath}/?`;
      if (window.location.href.includes(matchingPath)) {
        setSelectedCategory(cat.displayName);
      }
    });
  }, [relatedCategories]);

  useEffect(() => {
    const filterArray = Object.keys(filtersData);
    if (filterArray.length === 0) return;
    setFilterKeys(filterArray);
  }, [filtersData]);

  const navigate = (category, label, catPath) => {
    // get the current path from the last level of the breadcrumb
    const pathname = breadcrumbs?.slice(-1)[0]?.href;
    // just add the current category path if it's not in the higher hierarchy level (no parents)
    // to avoid duplicates, and if it's not in the lower level (no children)
    const parentCategoryPath = hasChildren && parentId ? `${categoryPath}/` : '';
    const newLocation = `${pathname}/${parentCategoryPath}${catPath}/?filter=${
      window.cards_page ? 'card' : 'invitation'
    }&selectedCat=${encodeURIComponent(label)}`;
    window.location.href = newLocation;
  };

  return (
    <>
      <div
        className={cx('gallery_top-filters', {
          'gallery_top-filters_seo': seoPage,
        })}
      >
        <Typography className="top-filters_label" variant="paragraph2" semibold>
          Filter by
        </Typography>
        {relatedCats.length > 0 && (
          <Input
            className="top-filters_select top-filters_select-category"
            data-qa-id="topFiltersRelatedCategorySelect"
            data-menu-qa-id="topFiltersRelatedCategoryMenu"
            error={null}
            isClearable={false}
            options={relatedCats}
            activeOption={selectedCategory}
            onChange={({id, label, value}) => navigate(id, label, value)}
            placeholder={selectedCategory || 'Related Categories'}
            type="select"
            value={selectedCategory}
            menuPortalTarget={document.body}
          />
        )}
        {filterKeys.map((f) => {
          const filterData = filtersData[f];
          if (JSON.stringify(filterData?.filters) === '{}') return null;

          return (
            <MultiSelect
              key={f}
              filterType={f}
              displayName={filterData?.display_name || ''}
              filter={filterData?.filters || {}}
              onToggle={onFilterDismiss}
              activeFilters={activeFilters}
              rightAlign={filterKeys[filterKeys.length - 1] === f}
            />
          );
        })}
      </div>
      <div
        className={cx('top-filters_footer', {
          'top-filters_footer-seo': seoPage,
        })}
      >
        <FilterPreviewer
          filters={activeFilters}
          onFilterDismiss={onFilterDismiss}
          onClearAllFilters={onClearAllFilters}
          is4AcrossABVariant
        />
        <div className="top-filters_footer_meta">
          {galleryUploadComponent}
          <Typography className="top-filters_results-count" variant="paragraph2">
            {numTemplates} items
          </Typography>
        </div>
      </div>
    </>
  );
};
